import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { lightImpactVibrate } from '../../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingScreenLoaded,
  trackTaxHarvestingButtonTap
} from '../../../tracking/taxHarvestingTracking'
import { roundOffAndFormatNumberWithCurrency } from '../../../../../utils/utils'
import { getTopPadding } from '../../../../../utils/sessionStorage'
import BackButton from '../../../../../components/ui-components/BackButton'
import { fetchTaxHarvestingRecommendation } from '../../../api/recommendationApi'
import {
  type TaxHarvestingRecommendationResponse,
  type FundSummaryMetrics,
  RecommendationVariant
} from '../../../types/recommendation.types'
import { HarvestingPlanType } from '../../../types/summary.types'
import { RecommendationPageHeader } from '../../../components/plan/RecommendationPageHeader'
import { FolioWithdrawSection } from './FolioWithdrawSection'
import { ReinvestSection } from './ReinvestSection'
import TaxHarvestingFooter from '../../../components/summary/TaxHarvestingFooter'
import ThreeLineLoading from '../../../../../components/ui-components/loading/ThreeLineLoading'

function parseAmount(value: string | null): number {
  if (value == null || value === '') return 0
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

export default function RecommendationPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isin = searchParams.get('isin') ?? ''
  const amount = parseAmount(searchParams.get('amount'))
  const saveTaxFromParams = parseAmount(searchParams.get('saveTax'))
  const harvestTypeParam = searchParams.get('harvestType') ?? ''
  const harvestType = Object.values(HarvestingPlanType).includes(harvestTypeParam as HarvestingPlanType)
    ? (harvestTypeParam as HarvestingPlanType)
    : null

  const possibleFolioTaxHarvestingTypesParam = searchParams.get('possibleFolioTaxHarvestingTypes') ?? ''
  const possibleFolioTaxHarvestingTypes: HarvestingPlanType[] =
    possibleFolioTaxHarvestingTypesParam === ''
      ? []
      : possibleFolioTaxHarvestingTypesParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => Object.values(HarvestingPlanType).includes(s as HarvestingPlanType)) as HarvestingPlanType[]

  const { data, isLoading, isError } = useQuery<TaxHarvestingRecommendationResponse>({
    queryKey: ['taxHarvestingRecommendation', isin, amount, harvestType, possibleFolioTaxHarvestingTypesParam],
    queryFn: () =>
      fetchTaxHarvestingRecommendation({
        isin,
        amount,
        harvestType: harvestType ?? HarvestingPlanType.EQ_LTCG,
        possibleFolioTaxHarvestingTypes: possibleFolioTaxHarvestingTypes.length > 0 ? possibleFolioTaxHarvestingTypes : undefined
      }),
    enabled: Boolean(isin && harvestType),
  })

  useEffect(() => {
    trackTaxHarvestingScreenLoaded(TaxHarvestingScreenName.Recommendation)
  }, [])

  const handleBack = () => {
    trackTaxHarvestingButtonTap({
      screen: TaxHarvestingScreenName.Recommendation,
      eventId: TaxHarvestingEventId.Back,
      buttonLabel: TaxHarvestingButtonLabel.Back
    })
    lightImpactVibrate?.()
    navigate(-1)
  }

  if (isLoading) {
    return (
      <ThreeLineLoading />
    )
  }

  if (isError || !data || !isin || !harvestType) {
    return (
      <div className="h-screen bg-white flex flex-col">
        <div
          style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
          className="shrink-0 pl-[19px] pr-5 pb-4"
        >
          <BackButton size={24} onClick={handleBack} />
        </div>
        <main className="flex-1 min-h-0 flex items-center justify-center px-5">
          <p className="text-[14px] font-[400] text-[#6A6E6E]">
            Something went wrong. Please try again.
          </p>
        </main>
      </div>
    )
  }

  const withdrawDetails = data?.withdrawDetails
  const hasFolios = (withdrawDetails?.length ?? 0) > 0
  const folioCount = withdrawDetails?.length ?? 0
  const totalWithdrawAmount = data?.totalWithdrawAmount

  const fundSummaryMetrics: FundSummaryMetrics = {
    currentValue: data?.currentValue,
    initialInvestment: data?.investedValue,
    xirr: data?.xirr,
    oneDayReturns: data?.oneDayReturns?.percentage,
    oneDayReturnsValue: data?.oneDayReturns?.value,
    returns: data?.returns?.percentage,
    returnsValue: data?.returns?.value
  }

  const saveTax = data?.saveTax || saveTaxFromParams
  const isFutureTax = data?.isFutureTax ?? false

  return (
    <div className="h-screen bg-white flex flex-col">
      <div
        style={{ paddingTop: `calc(${getTopPadding()}px + 8px)` }}
        className="shrink-0 pl-[19px] pr-5 pb-4"
      >
        <BackButton size={24} onClick={handleBack} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
        <RecommendationPageHeader
          isin={data?.isin ?? ''}
          variant={amount >= 0 ? RecommendationVariant.GAINS_TAX_FREE : RecommendationVariant.LOSSES_SAVE_TAX}
          headlineAmount={amount}
          saveTax={saveTax}
          isFutureTax={isFutureTax}
          fundLogo={data?.logo ?? ''}
          fundTitle={data?.name ?? ''}
          fundNature={data?.nature ?? ''}
          fundCategory={data?.category ?? ''}
          fundAmount={amount}
          fundSummaryMetrics={fundSummaryMetrics}
          powerRank={data?.powerRank ?? 0}
          powerRating={data?.powerRating ?? undefined}
          isRegular={data?.purchaseMode === 'REGULAR'}
        />
        <main className="px-5 pt-6 pb-12">
          <div className="flex flex-col gap-6 h-max">
            {hasFolios && (withdrawDetails?.length ?? 0) > 0 && (
              <FolioWithdrawSection
                stepNumber={1}
                title={`Withdraw ${roundOffAndFormatNumberWithCurrency(totalWithdrawAmount ?? 0)} from ${folioCount} folio${folioCount !== 1 ? 's' : ''}`}
                withdrawDetails={withdrawDetails ?? []}
                harvestType={harvestType ?? HarvestingPlanType.EQ_LTCG}
                isFutureTax={isFutureTax}
                isGains={amount >= 0}
              />
            )}
            <div
              className='px-4 pt-5 pb-6 flex flex-col gap-3 rounded-2xl bg-no-repeat bg-right-top border-grey80 border'
              style={{ backgroundImage: "url('/images/bg-cover-curves.png')" }}
            >
              <div className='text-13 font-medium'>Important Note</div>
              <div className='text-13'>The tax savings shown are <span className='font-medium'>after accounting for exit load.</span> We only recommend withdrawals where the benefit significantly outweighs the cost.</div>
            </div>

            <ReinvestSection
              stepNumber={hasFolios ? 2 : 1}
              totalWithdrawAmount={data?.reinvestAmount ?? 0}
              rebalanceSuggestions={data?.rebalanceSuggestions ?? []}
              directFundSuggestion={data?.directFundSuggestion ?? null}
              currentFundData={{
                isin: data?.isin ?? '',
                name: data?.name ?? '',
                nature: data?.nature ?? '',
                category: data?.category ?? '',
                logo: data?.logo ?? '',
                powerRank: data?.powerRank ?? 0,
                powerRating: data?.powerRating ?? undefined,
              }}
              isElss={data?.isElss ?? false}
            />

          </div>
        </main>
        <TaxHarvestingFooter />
      </div>
    </div>
  )
}
